import React from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';

const GithubIcon = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4" />
    <path d="M9 18c-4.51 2-5-2-7-2" />
  </svg>
);

const TwitterIcon = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />
  </svg>
);

const LinkedinIcon = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect width="4" height="12" x="2" y="9" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

const Footer = () => {
  return (
    <footer className="bg-white border-t border-gray-100 pt-16 pb-12">
      <div className="max-w-7xl mx-auto px-4 md:px-8 text-center md:text-left">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          <div className="col-span-1 md:col-span-1 flex flex-col items-center md:items-start">
            <Link to="/" className="text-2xl font-bold tracking-tight text-brand-indigo mb-6 block">Chooziq</Link>
            <p className="text-gray-500 font-medium text-sm leading-relaxed mb-8">
              The persistent realtime collaboration ecosystem where communities interact live and grow together.
            </p>
            <div className="flex items-center justify-center md:justify-start gap-4">
              <a href="https://github.com/chooziq" target="_blank" rel="noopener noreferrer" className="p-3 bg-gray-50 text-gray-400 hover:text-brand-indigo hover:bg-brand-indigo/5 rounded-2xl transition-all shadow-sm hover:shadow-md active:scale-90 group" aria-label="Github">
                <GithubIcon size={20} className="group-hover:scale-110 transition-transform" />
              </a>
              <a href="https://x.com/chooziq" target="_blank" rel="noopener noreferrer" className="p-3 bg-gray-50 text-gray-400 hover:text-brand-indigo hover:bg-brand-indigo/5 rounded-2xl transition-all shadow-sm hover:shadow-md active:scale-90 group" aria-label="X (Twitter)">
                <TwitterIcon size={20} className="group-hover:scale-110 transition-transform" />
              </a>
              <a href="https://linkedin.com/company/chooziq" target="_blank" rel="noopener noreferrer" className="p-3 bg-gray-50 text-gray-400 hover:text-brand-indigo hover:bg-brand-indigo/5 rounded-2xl transition-all shadow-sm hover:shadow-md active:scale-90 group" aria-label="Linkedin">
                <LinkedinIcon size={20} className="group-hover:scale-110 transition-transform" />
              </a>
            </div>
          </div>
          
          <div>
            <h4 className="font-bold text-gray-900 uppercase tracking-[0.15em] text-[10px] mb-8">Platform</h4>
            <ul className="space-y-4">
              <li><Link to="/live" className="text-gray-500 hover:text-brand-indigo font-medium text-sm transition-colors">Live Rooms</Link></li>
              <li><Link to="/explore" className="text-gray-500 hover:text-brand-indigo font-medium text-sm transition-colors">Explore</Link></li>
              <li><Link to="/faq" className="text-gray-500 hover:text-brand-indigo font-medium text-sm transition-colors">FAQ</Link></li>
              <li><Link to="/help" className="text-gray-500 hover:text-brand-indigo font-medium text-sm transition-colors">Help Center</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-gray-900 uppercase tracking-[0.15em] text-[10px] mb-8">Company</h4>
            <ul className="space-y-4">
              <li><Link to="/about" className="text-gray-500 hover:text-brand-indigo font-medium text-sm transition-colors">About Us</Link></li>
              <li><Link to="/contact" className="text-gray-500 hover:text-brand-indigo font-medium text-sm transition-colors">Contact Us</Link></li>
              <li><a href="mailto:contact@chooziq.com" className="text-gray-500 hover:text-brand-indigo font-medium text-sm transition-colors flex items-center justify-center md:justify-start gap-2">
                <Mail size={16} className="text-brand-indigo" /> Email Us
              </a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-gray-900 uppercase tracking-[0.15em] text-[10px] mb-8">Legal</h4>
            <ul className="space-y-4">
              <li><Link to="/guidelines" className="text-gray-500 hover:text-brand-indigo font-medium text-sm transition-colors">Guidelines</Link></li>
              <li><Link to="/content-policy" className="text-gray-500 hover:text-brand-indigo font-medium text-sm transition-colors">Content Policy</Link></li>
              <li><Link to="/privacy-policy" className="text-gray-500 hover:text-brand-indigo font-medium text-sm transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-gray-500 hover:text-brand-indigo font-medium text-sm transition-colors">Terms of Service</Link></li>
              <li><Link to="/disclaimer" className="text-gray-500 hover:text-brand-indigo font-medium text-sm transition-colors">Disclaimer</Link></li>
            </ul>
          </div>
        </div>
        
        <div className="pt-8 border-t border-gray-50 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 text-[10px] font-bold uppercase tracking-[0.15em] opacity-80">© 2026 Chooziq Inc. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
